<template>
  <div>
    <!-- Header -->
    <header class="header">
      <div class="header-container">
        <a href="/#/" class="logo-section" style="text-decoration:none; color:inherit; cursor:pointer;" title="Go to homepage">
          <img :src="county.logo || '/img/default-logo.png'" :alt="county.countyName" class="logo" />
          <div class="logo-text">
            <h1 class="county-name">{{ formatCountyName(county.countyName) }}</h1>
            <span class="portal-label">{{ $t('header.portalLabel') }}</span>
          </div>
        </a>

        <nav class="desktop-nav">
          <a href="/#services" class="nav-link">{{ $t('nav.services') }}</a>
          <a href="/#how-it-works" class="nav-link">{{ $t('nav.howItWorks') }}</a>
          <a href="/#contact" class="nav-link">{{ $t('nav.contact') }}</a>
          <a v-if="!isSignedIn" href="/#/sign-in" class="nav-link nav-link-signin">{{ $t('nav.signIn') }}</a>
          <a v-else href="/#/my-account" class="nav-link nav-link-signin">{{ $t('nav.myAccount') }}</a>
          <a href="/#print-receipt" class="btn-receipt">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            {{ $t('receipt.printReceipt') }}
          </a>
          <LanguageSwitcher class="header-lang-switcher" />
        </nav>

        <button class="mobile-menu-btn" @click="toggleMobileMenu" aria-label="Toggle menu">
          <span class="hamburger" :class="{ 'active': mobileMenuOpen }"></span>
        </button>
      </div>
    </header>

    <!-- Mobile Menu -->
    <transition name="slide">
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <nav class="mobile-nav">
          <a href="/#services" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('nav.services') }}</a>
          <a href="/#how-it-works" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('nav.howItWorks') }}</a>
          <a href="/#contact" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('nav.contact') }}</a>
          <a v-if="!isSignedIn" href="/#/sign-in" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('nav.signIn') }}</a>
          <a v-else href="/#/my-account" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('nav.myAccount') }}</a>
          <a href="/#print-receipt" class="btn-receipt mobile" @click="closeMobileMenu">{{ $t('receipt.printReceipt') }}</a>
          <div class="mobile-lang-row">
            <LanguageSwitcher />
          </div>
        </nav>
      </div>
    </transition>
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import * as session from "@/utils/citizenSession";

export default {
  name: "HeaderComponent",
  components: { LanguageSwitcher },
  computed: {
    isSignedIn() { return session.isAuthed(); },
  },
  data() {
    return {
      county: {
        id: null,
        countyID: null,
        countyName: null,
        tagline: null,
        contact: null,
        address: null,
        email: null,
        logo: null,
        bank: null,
        powered: null
      },
      mobileMenuOpen: false
    };
  },
  mounted() {
    this.getCounty();
  },
  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (error) {
        console.error('Error fetching county data:', error);
      }
    },

    formatCountyName(name) {
      if (!name) return '';
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },

  }
};
</script>

<style scoped>
/* Header */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border);
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  height: 48px;
  width: auto;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.county-name {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: var(--color-primary);
  line-height: 1.2;
}

.portal-label {
  font-size: 12px;
  color: var(--color-text-muted);
  font-weight: 500;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-link {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  transition: color 0.2s;
}

.nav-link:hover {
  color: var(--color-primary);
}

.btn-receipt {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-receipt:hover {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
}

.mobile-menu-btn {
  display: none;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
}

.hamburger {
  display: block;
  width: 24px;
  height: 2px;
  background: var(--color-text);
  position: relative;
  transition: all 0.3s;
}

.hamburger::before,
.hamburger::after {
  content: '';
  position: absolute;
  width: 24px;
  height: 2px;
  background: var(--color-text);
  left: 0;
  transition: all 0.3s;
}

.hamburger::before { top: -7px; }
.hamburger::after { top: 7px; }

.hamburger.active {
  background: transparent;
}

.hamburger.active::before {
  top: 0;
  transform: rotate(45deg);
}

.hamburger.active::after {
  top: 0;
  transform: rotate(-45deg);
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid var(--color-border);
  z-index: 999;
  padding: 24px;
}

.mobile-nav {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-nav-link {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border);
}

.btn-receipt.mobile {
  width: 100%;
  justify-content: center;
  margin-top: 8px;
  text-align: center;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }

  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-menu {
    display: block;
  }
}

@media (max-width: 480px) {
  .header-container {
    padding: 0 16px;
  }

  .logo-text {
    display: none;
  }
}

/* Language switcher — sits at the end of the desktop nav, and gets its own
   row inside the mobile menu so it doesn't crowd the vertical link list. */
.header-lang-switcher { margin-left: 12px; }
.mobile-lang-row {
  padding: 12px 20px;
  display: flex;
  justify-content: flex-start;
}
.nav-link-signin { font-weight: 600; }
</style>